import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {boolean} Flag to prevent scroll handler interference during programmatic transitions */
  #isTransitioning = false;

  /**
   * Swipe gesture state. Tracks a single primary pointer; multi-pointer
   * gestures (pinch-zoom) abort tracking so they don't interfere with the
   * drag-zoom-wrapper on mobile.
   * @type {{ pointerId: number | null, startX: number, startY: number, lastX: number, lastY: number, moved: boolean, locked: 'horizontal' | 'vertical' | null }}
   */
  #swipe = {
    pointerId: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    moved: false,
    locked: null,
  };

  /** @type {boolean} Suppress the synthetic click that follows a horizontal swipe so the dialog doesn't close. */
  #suppressNextClick = false;

  /** Minimum horizontal travel (px) to register a navigation swipe. */
  static SWIPE_THRESHOLD = 50;
  /** Axis lock-in threshold (px) — once exceeded we decide horizontal vs vertical and stick with it. */
  static SWIPE_AXIS_LOCK = 8;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.#bindSwipeListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.#unbindSwipeListeners();
  }

  #bindSwipeListeners() {
    const { dialog } = this.refs;
    dialog.addEventListener('pointerdown', this.#handlePointerDown);
    dialog.addEventListener('pointermove', this.#handlePointerMove);
    dialog.addEventListener('pointerup', this.#handlePointerUp);
    dialog.addEventListener('pointercancel', this.#handlePointerCancel);
    dialog.addEventListener('click', this.#handleClickCapture, true);
  }

  #unbindSwipeListeners() {
    const { dialog } = this.refs;
    dialog.removeEventListener('pointerdown', this.#handlePointerDown);
    dialog.removeEventListener('pointermove', this.#handlePointerMove);
    dialog.removeEventListener('pointerup', this.#handlePointerUp);
    dialog.removeEventListener('pointercancel', this.#handlePointerCancel);
    dialog.removeEventListener('click', this.#handleClickCapture, true);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    // Skip scroll handling during programmatic transitions to prevent interference
    if (this.#isTransitioning) return;

    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Handles keyboard navigation inside the dialog.
   *  - Escape closes the dialog.
   *  - ArrowLeft / ArrowRight navigate between media (same slide animation as swipe).
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#navigate('previous');
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#navigate('next');
    }
  }

  /**
   * Begin tracking a potential swipe. Only the primary pointer is tracked;
   * a second pointer (pinch) aborts tracking so drag-zoom-wrapper can take over.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Ignore gestures that start on thumbnails or any control button.
    if (target.closest('.dialog-thumbnails-list-container')) return;
    if (target.closest('button')) return;
    if (!target.closest('.product-media-container')) return;

    // Second pointer arrives mid-gesture → abort (pinch/zoom in drag-zoom-wrapper).
    if (this.#swipe.pointerId !== null) {
      this.#resetSwipe();
      return;
    }

    // Skip when the image is currently zoomed in via drag-zoom-wrapper to avoid
    // hijacking the pan gesture on mobile.
    if (this.#isImageZoomedIn()) return;

    this.#swipe.pointerId = event.pointerId;
    this.#swipe.startX = event.clientX;
    this.#swipe.startY = event.clientY;
    this.#swipe.lastX = event.clientX;
    this.#swipe.lastY = event.clientY;
    this.#swipe.moved = false;
    this.#swipe.locked = null;
  };

  /**
   * Track the pointer and decide whether the gesture is horizontal.
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (this.#swipe.pointerId === null || event.pointerId !== this.#swipe.pointerId) return;

    this.#swipe.lastX = event.clientX;
    this.#swipe.lastY = event.clientY;

    const deltaX = event.clientX - this.#swipe.startX;
    const deltaY = event.clientY - this.#swipe.startY;

    if (!this.#swipe.locked) {
      if (Math.abs(deltaX) > ZoomDialog.SWIPE_AXIS_LOCK || Math.abs(deltaY) > ZoomDialog.SWIPE_AXIS_LOCK) {
        this.#swipe.locked = Math.abs(deltaX) >= Math.abs(deltaY) ? 'horizontal' : 'vertical';
        this.#swipe.moved = true;
      }
    }
  };

  /**
   * Decide whether the gesture qualifies as a navigation swipe and act on it.
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (this.#swipe.pointerId === null || event.pointerId !== this.#swipe.pointerId) return;

    const deltaX = this.#swipe.lastX - this.#swipe.startX;
    const isHorizontalSwipe =
      this.#swipe.locked === 'horizontal' && Math.abs(deltaX) >= ZoomDialog.SWIPE_THRESHOLD;

    if (isHorizontalSwipe) {
      // Block the synthetic click that follows on the same target so it doesn't trigger
      // the `on:click="/close"` handler attached to image containers.
      this.#suppressNextClick = true;
      const direction = deltaX < 0 ? 'next' : 'previous';
      this.#navigate(direction);
    } else if (this.#swipe.moved) {
      // Even a sub-threshold drag should not register as a "click to close".
      this.#suppressNextClick = true;
    }

    this.#resetSwipe();
  };

  /** @param {PointerEvent} event */
  #handlePointerCancel = (event) => {
    if (this.#swipe.pointerId === null || event.pointerId !== this.#swipe.pointerId) return;
    this.#resetSwipe();
  };

  /**
   * Capture-phase click handler that swallows the click immediately following a swipe.
   * @param {MouseEvent} event
   */
  #handleClickCapture = (event) => {
    if (!this.#suppressNextClick) return;
    this.#suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  #resetSwipe() {
    this.#swipe.pointerId = null;
    this.#swipe.startX = 0;
    this.#swipe.startY = 0;
    this.#swipe.lastX = 0;
    this.#swipe.lastY = 0;
    this.#swipe.moved = false;
    this.#swipe.locked = null;
  }

  /**
   * Returns true if the currently active media's drag-zoom-wrapper has been
   * pinched past its default zoom level — in that state we defer the gesture
   * to drag-zoom-wrapper so the user can pan the zoomed image without our
   * swipe handler stealing the release as a navigation.
   *
   * The wrapper's `DEFAULT_ZOOM` is 1.5 (set on mobile init), so we use a
   * threshold just above that. On desktop the wrapper never writes the CSS
   * variable, so this returns false and swipe always works.
   * @returns {boolean}
   */
  #isImageZoomedIn() {
    const { media } = this.refs;
    const active = media.find((m) => m.classList.contains('product-media--active'));
    const wrapper = active?.querySelector('drag-zoom-wrapper');
    if (!(wrapper instanceof HTMLElement)) return false;
    const raw = wrapper.style.getPropertyValue('--drag-zoom-scale');
    const scale = parseFloat(raw);
    return Number.isFinite(scale) && scale > 1.51;
  }

  /**
   * Navigate to the previous or next image with a slide animation.
   * Skipped silently if there is no neighbour in that direction.
   * @param {'next' | 'previous'} direction
   */
  #navigate(direction) {
    if (this.#isTransitioning) return;

    const { media } = this.refs;
    if (!media.length) return;

    const currentIndex = media.findIndex((m) => m.classList.contains('product-media--active'));
    if (currentIndex < 0) return;

    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex < 0 || newIndex >= media.length) return;

    this.selectThumbnail(newIndex, { behavior: 'smooth', transition: direction });
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} [options.behavior] - The behavior of the scroll.
   * @param {'next' | 'previous'} [options.transition] - If set, use a horizontal slide animation
   *   in the given direction instead of the default cross-fade. Used by swipe + arrow-key navigation.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { dialog, media, thumbnails } = this.refs;
    const hasThumbnails = thumbnails?.children?.length > 0;

    // Guard if invalid index (use media.length when no thumbnails, e.g. single image)
    const maxIndex = hasThumbnails ? thumbnails.children.length : media.length;
    if (isNaN(index) || index < 0 || index >= maxIndex) return;

    const targetThumbnail = hasThumbnails ? thumbnails.children[index] : null;
    if (hasThumbnails && (!targetThumbnail || !(targetThumbnail instanceof HTMLElement))) return;

    // Prevent scroll handler from interfering during the transition
    this.#isTransitioning = true;

    if (hasThumbnails) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      });

      // Scroll the thumbnail strip (this does not affect the dialog scroll)
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    // --- Begin layout changes with scroll position lock ---
    // Save dialog scroll position before any layout changes
    const savedScrollTop = dialog.scrollTop;

    const activeMedia = media.find((m) => m.classList.contains('product-media--active'));
    const targetImage = media[index];
    const direction = options.transition === 'next' || options.transition === 'previous' ? options.transition : null;

    if (direction && activeMedia && targetImage && activeMedia !== targetImage) {
      // Horizontal slide transition (swipe / arrow keys). Both images stay in
      // the layout simultaneously by being absolutely positioned over their
      // shared `<ul class="dialog-zoomed-gallery">` parent.
      this.#slideTransition(activeMedia, targetImage, direction);
    } else {
      // Default fade transition (thumbnail click, programmatic select).
      if (activeMedia && activeMedia !== targetImage) {
        activeMedia.classList.remove('product-media--active');
        activeMedia.style.opacity = '0';
        activeMedia.style.display = 'none';
      }

      if (targetImage) {
        targetImage.classList.add('product-media--active');
        targetImage.style.display = 'block';
        targetImage.style.opacity = '0';
        this.fadeIn(targetImage);
      }
    }

    // Restore dialog scroll position immediately after all layout changes
    // to prevent any scroll jump caused by the DOM reflow
    dialog.scrollTop = savedScrollTop;
    // --- End layout changes ---

    if (targetImage) {
      this.#loadHighResolutionImage(targetImage);
    }

    // Re-enable scroll handler after fade animation completes
    setTimeout(() => {
      this.#isTransitioning = false;
    }, 450);

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Slide the active image out and the target image in horizontally.
   * Both elements are temporarily positioned absolutely so they can overlap;
   * inline styles are cleared on completion so the regular gallery CSS resumes.
   *
   * @param {HTMLElement} fromEl - The currently-active media element.
   * @param {HTMLElement} toEl - The media element to slide in.
   * @param {'next' | 'previous'} direction - 'next' slides right→left, 'previous' slides left→right.
   */
  #slideTransition(fromEl, toEl, direction) {
    const duration = 320;
    const enterFrom = direction === 'next' ? '100%' : '-100%';
    const exitTo = direction === 'next' ? '-100%' : '100%';

    const overlayStyles = 'position:absolute;inset:0;width:100%;height:100%;will-change:transform;';

    // Prepare the incoming element off-screen.
    toEl.classList.add('product-media--active');
    toEl.setAttribute('style', `${overlayStyles}transition:none;transform:translateX(${enterFrom});opacity:1;display:block;`);

    // Pin the outgoing element in place so it can slide.
    fromEl.setAttribute('style', `${overlayStyles}transition:none;transform:translateX(0);opacity:1;display:block;`);

    // Force a reflow so the initial transform sticks before transitioning.
    void toEl.offsetWidth;

    const easing = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
    toEl.style.transition = `transform ${duration}ms ${easing}`;
    toEl.style.transform = 'translateX(0)';
    fromEl.style.transition = `transform ${duration}ms ${easing}`;
    fromEl.style.transform = `translateX(${exitTo})`;

    const cleanup = () => {
      fromEl.removeEventListener('transitionend', cleanup);
      fromEl.classList.remove('product-media--active');
      fromEl.removeAttribute('style');
      fromEl.style.display = 'none';

      toEl.removeAttribute('style');
      toEl.style.display = 'block';
    };

    fromEl.addEventListener('transitionend', cleanup, { once: true });
    // Safety net in case transitionend doesn't fire (e.g. tab backgrounded).
    setTimeout(cleanup, duration + 80);
  }

  /**
   * Fades in an element.
   * @param {HTMLElement} el - The element to fade in.
   * @param {number} duration - The duration of the fade in milliseconds.
   */

  fadeIn(el, duration = 400) {
    el.style.opacity = 0;
    el.style.display = 'block';

    let start = null;

    function animate(timestamp) {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      el.style.opacity = Math.min(progress / duration, 1);

      if (progress < duration) {
        requestAnimationFrame(animate);
      }
    }

    requestAnimationFrame(animate);
  }

  /**
   * Fades out an element.
   * @param {HTMLElement} el - The element to fade out.
   * @param {number} duration - The duration of the fade out milliseconds.
   */
  fadeOut(el, duration = 400) {
    let start = null;
    const initialOpacity = 1;

    function animate(timestamp) {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      el.style.opacity = Math.max(1 - progress / duration, 0);

      if (progress < duration) {
        requestAnimationFrame(animate);
      } else {
        el.style.display = 'none';
      }
    }

    requestAnimationFrame(animate);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */(mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
